import { Alert, AppLayout, Box, Button, SpaceBetween } from '@amzn/awsui-components-core';
import AnchorNavigation, { AnchorNavigationProps } from '@amzn/awsui-components-core/anchor-navigation';
import { Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useActiveAnchor } from './useActiveAnchor';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LoadingStatus } from 'src/components/context/AppContextModels';
import { useAppContext } from 'src/components/context/AppContextProvider';
import { ConfirmationModal } from 'src/components/generic-components/ConfirmationModal';
import DisplayContentInCode from 'src/components/generic-components/DisplayContentInCode';
import { appLayoutAriaLabels } from 'src/i18n-strings';
import { ApplicationFormValues } from '../../AdminModels';
import { AdminSideNavigationV2 } from '../../AdminSideNavigationV2';
import ApplicationDetails from './ApplicationDetailsForm';
import { apiToFormValues, INITIAL_FORM_VALUES, useApplicationForm } from './ApplicationFormUtilities';
import { ApplicationFormSchema } from './ApplicationFormValidations';
import { fetchGraphQLRequest } from 'src/api/graphql-api';
import { appSyncOperationName } from 'src/constants/AppConstants';
import { listMcpConfigs } from 'src/graphql/queries';
import { McpConfig } from 'src/components/context/AppContextModels';
import { logger } from 'src/logger';

import './AdminNewApp.scss';

const STICKY_HEADER_HEIGHT = 56;

const FORM_ANCHORS: AnchorNavigationProps.Anchor[] = [
  { text: 'Application Overview', href: '#application-overview', level: 1 },
  { text: 'Application Logo', href: '#application-logo', level: 1 },
  { text: 'Q Chat Configuration', href: '#q-chat-configuration', level: 1 },
  { text: 'MCP Server', href: '#mcp-configuration', level: 1 },
  { text: 'Application Links', href: '#application-links', level: 1 }
];

const ANCHOR_IDS = FORM_ANCHORS.map((a) => a.href.replace('#', ''));
const ACTIVE_ANCHOR_OPTIONS = { anchorIds: ANCHOR_IDS, defaultHref: FORM_ANCHORS[0].href, stickyOffset: STICKY_HEADER_HEIGHT };

export const AdminNewApp: React.FC = () => {
  const { id: applicationId } = useParams();
  const { pathname, state } = useLocation();
  const appContext = useAppContext();
  const existingApplicationNames = appContext?.listOfApplications?.map((application) => application.applicationName || '');
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { activeHref, handleAnchorClick } = useActiveAnchor(contentRef, ACTIVE_ANCHOR_OPTIONS);

  useEffect(() => {
    setFromUrl(state?.from ? state?.from : '');
  }, [pathname, state]);

  const {
    stepError,
    setStepError,
    action,
    setAction,
    applicationFormRef,
    onFormCancel,
    onSubmit,
    showModalInfo,
    onCancel,
    onConfirm,
    fromUrl,
    setFromUrl
  } = useApplicationForm();

  const [initialFormValues, setInitialFormValues] = useState<ApplicationFormValues>(INITIAL_FORM_VALUES);

  useEffect(() => {
    if (appContext.contextLoadingStatus === LoadingStatus.Completed) {
      if (applicationId) {
        setAction('Update');
        handleEditApplication(applicationId);
      } else {
        setAction('Create');
        setInitialFormValues(INITIAL_FORM_VALUES);
      }
    }
  }, [applicationId, appContext]);

  const handleEditApplication = async (applicationId: string) => {
    const selectedApplication = appContext.listOfApplications.find((application) => application.id === applicationId);
    if (!selectedApplication) {
      navigate('/admin/manage-applications');
      return;
    }

    const formValues = apiToFormValues(selectedApplication, appContext.teamPageContent);

    // Only fetch MCP config if the app has mcpPath set (indicates MCP is configured)
    if (selectedApplication.mcpPath) {
      try {
        const mcpId = selectedApplication.id || '';
        const data = await fetchGraphQLRequest<{ listMcpConfigs: McpConfig[] }>({
          query: listMcpConfigs,
          variables: {},
          requestName: 'listMcpConfigs',
          requestType: appSyncOperationName.QUERY
        });
        const config = (data.listMcpConfigs || []).find((c) => c.id === mcpId);
        if (config) {
          formValues.applicationDetails.mcpConfig = {
            isEnabled: true,
            description: config.description || '',
            category: config.category || '',
            isFeatured: config.isFeatured || false,
            terminalCommands: config.terminalCommands || [],
            resources: (config.resources || []).map((r) => ({ label: r.label, url: r.url, type: r.type || '' })),
            toolGroups: (config.toolGroups || []).map((g) => ({
              category: g.category,
              tools: g.tools.map((t) => ({ name: t.name, description: t.description || '' }))
            })),
            features: config.features || [],
            contacts: config.contacts?.map((c) => ({ alias: c.alias, role: c.role || '' })) || []
          };
        }
      } catch (error) {
        logger.error('Failed to load MCP config for editing.', { error, applicationId });
        formValues.applicationDetails.mcpConfig = null;
      }
    }

    setInitialFormValues(formValues);
  };

  return (
    <>
      <ConfirmationModal showModalInfo={showModalInfo} onConfirm={onConfirm} onCancel={onCancel} />
      <AppLayout
        ariaLabels={appLayoutAriaLabels}
        navigation={<AdminSideNavigationV2 />}
        contentType="default"
        toolsHide={true}
        content={
          <Formik<ApplicationFormValues>
            innerRef={applicationFormRef}
            enableReinitialize
            initialValues={initialFormValues}
            validationSchema={ApplicationFormSchema(existingApplicationNames, initialFormValues.applicationDetails.applicationName !== '')}
            onSubmit={onSubmit}
          >
            {(formik) => (
              <form onSubmit={formik.handleSubmit}>
                <div className="admin-app-form__sticky-header">
                  <Box variant="h1" fontSize="heading-xl" fontWeight="bold">
                    {`${action} Application`}
                  </Box>
                  <SpaceBetween size="xs" direction="horizontal">
                    <Button variant="link" formAction="none" onClick={onFormCancel}>
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      formAction="none"
                      onClick={onSubmit}
                      disabled={!formik.dirty}
                      disabledReason="No changes have been made to the form."
                    >
                      Submit
                    </Button>
                  </SpaceBetween>
                </div>

                {stepError && (
                  <Alert type="error" dismissible onDismiss={() => setStepError('')}>
                    {stepError}
                  </Alert>
                )}

                <div className="admin-app-form__body" ref={contentRef}>
                  <div className="admin-app-form__anchor-nav">
                    <AnchorNavigation
                      anchors={FORM_ANCHORS}
                      activeHref={activeHref}
                      onFollow={(event) => {
                        event.preventDefault();
                        handleAnchorClick(event.detail.href);
                      }}
                    />
                  </div>
                  <div className="admin-app-form__content">
                    <ApplicationDetails formik={formik} />
                  </div>
                </div>
              </form>
            )}
          </Formik>
        }
      />
    </>
  );
};
